/**
 * EstimatePricingSnapshotPanel — shows the pricing snapshot frozen into an
 * estimate, plus a drift indicator if the workspace's current settings
 * would produce different numbers.
 *
 * For Draft/Review estimates, exposes a "Refresh from current settings"
 * action. Locked once the estimate is Sent/Accepted to preserve what the
 * client agreed to.
 */

import React, { useState } from 'react';
import { fechaCorta } from '../../utils/fechas';
import { useIdioma } from '../../i18n/idioma';
import { AlertTriangle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { PricingSnapshot, EstimateStatus } from '../../types';
import { canEditPricing } from '../../utils/estimateEditability';
import { useSettings } from '../../contexts/SettingsContext';
import { Button } from '../ui/Button';
import { StatusDot } from '../ui/StatusDot';

interface Props {
  snapshot: PricingSnapshot;
  status: EstimateStatus;
  onRefresh: () => void;
  /** Start collapsed to reduce noise in the estimate builder. */
  defaultCollapsed?: boolean;
}

const fmt = (currency: string, v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

const EstimatePricingSnapshotPanel: React.FC<Props> = ({ snapshot, status, onRefresh, defaultCollapsed = false }) => {
    const { t } = useIdioma();
  const { settings, metrics } = useSettings();
  const currency = settings.profile.currency || 'USD';
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  // La regla de «estimado cerrado» ya no se escribe aquí: nació en este
  // componente y por eso solo protegía este botón, mientras el dial de margen y
  // la puerta del constructor re-tarifaban un aceptado sin preguntar. Ahora vive
  // en utils/estimateEditability.ts y la comparten las tres puertas. El
  // comportamiento de este panel es EL MISMO que antes (Draft/Review abiertos).
  const canRefresh = canEditPricing(status);

  const currentBE = metrics.breakEvenRate ?? 0;
  const snapBE = snapshot.breakEvenRate ?? 0;
  const drift = currentBE - snapBE;
  const driftPct = snapBE > 0 ? Math.abs(drift / snapBE) * 100 : 0;
  const significantDrift = driftPct > 2;

  const capturedAt = snapshot.capturedAt ? new Date(snapshot.capturedAt) : null;

  return (
    <div className="bg-white border border-acero-200 rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setCollapsed(c => !c)}
        className="w-full px-4 py-2.5 flex items-center justify-between gap-3 text-left hover:bg-acero-50 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
      >
        <div className="min-w-0">
          <p className="text-xs text-navy-950 truncate">
            {t('estimado.snapshot.tarifas')} <span className="font-data tabular-nums">{fmt(currency, snapBE)}</span>{t('estimado.snapshot.hr_punto_equilibrio')} · <span className="font-data tabular-nums">{snapshot.targetMarginPercent}%</span>{t('estimado.snapshot.margen_sufijo')}
          </p>
          {!collapsed && capturedAt && (
            <p className="text-[11px] text-acero-500 mt-0.5">
              {t('estimado.snapshot.tomado_el')} <span className="font-data tabular-nums">{fechaCorta(capturedAt)}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* "Drift" no le dice nada a un contratista, y con el panel plegado
              —que es como arranca— esa palabra suelta era la UNICA senal de que
              el presupuesto se esta calculando con precios viejos. Nadie sabe
              que significa, asi que nadie abre el panel. Ahora lo dice, y dice
              desde cuando. */}
          {significantDrift && (
            <StatusDot tone="aviso">
              <span className="text-xs">
                Precios viejos{capturedAt && ` · ${fechaCorta(capturedAt)}`}
              </span>
            </StatusDot>
          )}
          {collapsed ? <ChevronDown size={14} className="text-acero-400" /> : <ChevronUp size={14} className="text-acero-400" />}
        </div>
      </button>

      {!collapsed && (
        <>
          <div className="px-4 py-2 border-t border-acero-100 bg-acero-50 flex items-center justify-between gap-2">
            <p className="label-ui">{t('estimado.snapshot.precios_usados')}</p>
            {canRefresh && significantDrift && (
              <Button variant="outline" size="sm" onClick={onRefresh} leftIcon={<RefreshCw size={12} />}>
              {t('estimado.snapshot.boton_actualizar')}
              </Button>
            )}
          </div>

          {significantDrift && (
            <div className="px-4 py-2.5 bg-aviso-bg border-b border-aviso/25 flex items-start gap-2">
              <AlertTriangle size={14} className="text-aviso shrink-0 mt-0.5" />
              <p className="text-[11px] text-aviso leading-snug">
                {t('estimado.snapshot.aviso_equilibrio')} <span className="font-data tabular-nums">{fmt(currency, currentBE)}</span>{t('estimado.snapshot.aviso_antes')} <span className="font-data tabular-nums">{fmt(currency, snapBE)}</span>/hr).
                {canRefresh ? t('estimado.snapshot.actualizar') : t('estimado.snapshot.bloqueado')}
              </p>
            </div>
          )}

          <div className="px-4 py-3 grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="label-ui mb-0.5">{t('estimado.snapshot.equilibrio_hr')}</p>
              <p className="font-data tabular-nums font-medium text-navy-950 text-sm">{fmt(currency, snapBE)}</p>
            </div>
            <div>
              <p className="label-ui mb-0.5">{t('estimado.snapshot.meta_hr')}</p>
              <p className="font-data tabular-nums font-medium text-navy-950 text-sm">{fmt(currency, snapshot.targetHourlyRate)}</p>
            </div>
            <div>
              <p className="label-ui mb-0.5">{t('estimado.snapshot.overhead_hr')}</p>
              <p className="font-data tabular-nums font-medium text-navy-950 text-sm">{fmt(currency, snapshot.overheadPerManHour)}</p>
            </div>
            <div>
              <p className="label-ui mb-0.5">{t('estimado.snapshot.margen')}</p>
              <p className="font-data tabular-nums font-medium text-navy-950 text-sm">{snapshot.targetMarginPercent}%</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EstimatePricingSnapshotPanel;
